import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, setDoc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';

const AuthContext = createContext({});

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const ensureProviderDoc = async (user) => {
        if (!user) return;

        const providerRef = doc(db, 'serviceProviders', user.uid);
        const providerSnap = await getDoc(providerRef);
        if (providerSnap.exists()) return;

        // Auto-heal legacy data: if an existing provider profile is stored under
        // a non-UID doc id but has the same email, migrate it to UID-based id.
        const email = (user.email || '').trim().toLowerCase();
        if (email) {
            const q = query(collection(db, 'serviceProviders'), where('email', '==', email));
            const byEmailSnap = await getDocs(q);
            const legacyMatch = byEmailSnap.docs.find(d => d.id !== user.uid);

            if (legacyMatch) {
                const legacyData = legacyMatch.data() || {};
                await setDoc(providerRef, {
                    ...legacyData,
                    uid: user.uid,
                    email,
                    migratedFromDocId: legacyMatch.id,
                    migratedAt: new Date().toISOString(),
                }, { merge: true });
                return;
            }
        }

        await setDoc(providerRef, {
            uid: user.uid,
            name: user.displayName || '',
            email: user.email || '',
            phone: '',
            isVerified: false,
            isActive: false,
            rating: 0,
            completedJobs: 0,
            createdAt: new Date().toISOString()
        }, { merge: true });
    };

    // Sign up with email and password (simple registration, no service details)
    const signup = async (email, password, name, phone, serviceCategory, experience) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);

            // Update user profile with name
            await updateProfile(result.user, {
                displayName: name
            });

            // Store as inactive/unverified service provider
            await setDoc(doc(db, 'serviceProviders', result.user.uid), {
                uid: result.user.uid,
                name,
                email,
                phone,
                serviceCategory: serviceCategory || '',
                experience: Number(experience) || 0,
                isVerified: false,
                isActive: false,
                rating: 0,
                completedJobs: 0,
                createdAt: new Date().toISOString()
            });

            return result;
        } catch (error) {
            throw error;
        }
    };

    // Sign in with email and password
    const login = async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        await ensureProviderDoc(result.user);
        return result;
    };

    // Logout
    const logout = () => {
        return signOut(auth);
    };

    // Get service provider profile
    const getProviderProfile = async (uid) => {
        try {
            const docRef = doc(db, 'serviceProviders', uid);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return docSnap.data();
            }
            return null;
        } catch (error) {
            throw error;
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    await ensureProviderDoc(user);
                } catch (error) {
                    console.error('Failed to ensure provider profile:', error);
                }
            }
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        signup,
        login,
        logout,
        getProviderProfile,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
